import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CheckSquare,
  Loader2,
  Clock,
  AlertTriangle,
  ChevronRight,
  Filter,
  Flag,
} from "lucide-react";
import { toast } from "sonner";
import { myTasksApi, plansApi } from "@/lib/api";
import useRefreshOnFocus from "@/lib/useRefreshOnFocus";

// MyTasksPage — cross-project personal task feed (Feb 2026).
// Overdue first, then due-soon, then due-date ascending (no due ⇒ last).
// All roles can use it; backend filters by assigned_user_id == current user.

const PHASE_PILL = {
  pre: "bg-teal-500/15 text-teal-300 border-teal-500/40",
  prod: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  post: "bg-violet-500/15 text-violet-300 border-violet-500/40",
};

const STATUS_BADGE = {
  late: { label: "LATE", cls: "bg-rose-500/15 text-rose-300 border-rose-500/40" },
  due_soon: { label: "DUE SOON", cls: "bg-amber-500/15 text-amber-300 border-amber-500/40" },
  in_progress: { label: "IN PROGRESS", cls: "bg-sky-500/15 text-sky-300 border-sky-500/40" },
  todo: { label: "TO DO", cls: "bg-zinc-800 text-zinc-400 border-zinc-700" },
  done: { label: "DONE", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
};

const DUE_WINDOWS = [
  { id: "all", label: "All" },
  { id: "week", label: "This week" },
  { id: "month", label: "This month" },
  { id: "overdue", label: "Overdue only" },
];

function offsetLabel(t) {
  if (t.status === "done") return "";
  if (t.days_offset == null) return "no deadline";
  if (t.days_offset < 0) return `${Math.abs(t.days_offset)}d overdue`;
  if (t.days_offset === 0) return "due today";
  if (t.days_offset === 1) return "due tomorrow";
  return `due in ${t.days_offset}d`;
}

export default function MyTasksPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [includeDone, setIncludeDone] = useState(false);
  const [projectFilter, setProjectFilter] = useState("all");
  const [dueWindow, setDueWindow] = useState("all");
  const [toggling, setToggling] = useState(null);

  const fetchTasks = () => {
    let cancelled = false;
    myTasksApi
      .list({ includeDone })
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  };
  useRefreshOnFocus(fetchTasks, [includeDone]);

  const tasks = data?.tasks || [];
  const projects = useMemo(() => {
    const m = new Map();
    for (const t of tasks) {
      if (t.project_id) m.set(t.project_id, t.project_name);
    }
    return Array.from(m.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [tasks]);

  const visible = useMemo(() => {
    let out = tasks;
    if (projectFilter !== "all") out = out.filter((t) => t.project_id === projectFilter);
    if (dueWindow === "overdue") out = out.filter((t) => t.status === "late");
    else if (dueWindow === "week") {
      out = out.filter((t) => t.days_offset != null && t.days_offset >= -30 && t.days_offset <= 7);
    } else if (dueWindow === "month") {
      out = out.filter((t) => t.days_offset != null && t.days_offset >= -30 && t.days_offset <= 31);
    }
    return out;
  }, [tasks, projectFilter, dueWindow]);

  const counts = data?.counts || {};

  const handleToggleComplete = async (task) => {
    const next = task.kanban_status === "done" ? "todo" : "done";
    setToggling(task.id);
    // Optimistic local update
    setData((prev) => prev && {
      ...prev,
      tasks: prev.tasks.map((t) =>
        t.id === task.id ? { ...t, kanban_status: next, status: next === "done" ? "done" : t.status } : t,
      ),
    });
    try {
      await plansApi.updateTask(task.id, { kanban_status: next });
      toast.success(next === "done" ? `"${task.name}" complete` : `"${task.name}" reopened`);
      // Refresh to recompute statuses + sort
      myTasksApi.list({ includeDone }).then(setData).catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update task");
      setData((prev) => prev && {
        ...prev,
        tasks: prev.tasks.map((t) =>
          t.id === task.id ? { ...t, kanban_status: task.kanban_status, status: task.status } : t,
        ),
      });
    } finally {
      setToggling(null);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-8" data-testid="my-tasks-page">
      {/* Header */}
      <div className="mb-6">
        <div className="text-overline text-amber-500/80 mb-1 flex items-center gap-1.5">
          <CheckSquare className="w-3 h-3" /> Workspace
        </div>
        <h1 className="font-heading text-4xl font-light text-zinc-100 leading-tight">
          My Tasks
        </h1>
        <p className="text-sm text-zinc-500 mt-2">
          Every task assigned to you, across every project. Overdue first.
        </p>
      </div>

      {/* Stat strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-5">
        <StatCard label="Late" value={counts.late || 0} tone="rose" testId="my-tasks-stat-late" />
        <StatCard label="Due soon" value={counts.due_soon || 0} tone="amber" testId="my-tasks-stat-due-soon" />
        <StatCard label="In progress" value={counts.in_progress || 0} tone="sky" testId="my-tasks-stat-in-progress" />
        <StatCard label="To do" value={counts.todo || 0} tone="zinc" testId="my-tasks-stat-todo" />
        <StatCard label="Done" value={counts.done || 0} tone="emerald" testId="my-tasks-stat-done" />
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap mb-3 text-xs">
        <Filter className="w-3.5 h-3.5 text-zinc-500" />
        <select
          data-testid="my-tasks-project-filter"
          value={projectFilter}
          onChange={(e) => setProjectFilter(e.target.value)}
          className="bg-zinc-950 border border-zinc-800 rounded-sm h-8 px-2 text-xs font-mono text-zinc-300 hover:border-zinc-700"
        >
          <option value="all">All projects</option>
          {projects.map(([id, name]) => (
            <option key={id} value={id}>{name}</option>
          ))}
        </select>
        <div className="inline-flex border border-zinc-800 rounded-sm overflow-hidden h-8" data-testid="my-tasks-window-group">
          {DUE_WINDOWS.map((w) => (
            <button
              key={w.id}
              type="button"
              onClick={() => setDueWindow(w.id)}
              data-testid={`my-tasks-window-${w.id}`}
              aria-pressed={dueWindow === w.id}
              className={`px-3 text-[11px] font-mono uppercase tracking-wide transition-colors ${
                dueWindow === w.id
                  ? "bg-amber-500/15 text-amber-300"
                  : "text-zinc-500 hover:text-amber-400"
              }`}
            >
              {w.label}
            </button>
          ))}
        </div>
        <label className="ml-auto inline-flex items-center gap-2 text-xs text-zinc-400 cursor-pointer select-none" data-testid="my-tasks-include-done">
          <input
            type="checkbox"
            checked={includeDone}
            onChange={(e) => setIncludeDone(e.target.checked)}
            className="accent-amber-500"
          />
          Show completed
        </label>
      </div>

      {/* Table */}
      <div className="border border-zinc-800 rounded-md overflow-hidden">
        {loading ? (
          <div className="py-16 flex items-center justify-center text-zinc-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading your tasks…
          </div>
        ) : visible.length === 0 ? (
          <div className="py-16 text-center" data-testid="my-tasks-empty">
            <CheckSquare className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
            <p className="text-sm text-zinc-400">
              {tasks.length === 0
                ? "Nothing on your plate. Enjoy the silence."
                : "No tasks match the current filters."}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-zinc-800/70" data-testid="my-tasks-list">
            {visible.map((t) => {
              const isDone = t.kanban_status === "done";
              const sb = STATUS_BADGE[t.status] || STATUS_BADGE.todo;
              const phaseCls = PHASE_PILL[t.phase] || "bg-zinc-800 text-zinc-400 border-zinc-700";
              return (
                <li
                  key={t.id}
                  className="flex items-center gap-3 px-3 py-2.5 hover:bg-zinc-900/40 transition-colors group"
                  data-testid={`my-tasks-row-${t.id}`}
                >
                  <button
                    type="button"
                    aria-label={isDone ? "Mark as todo" : "Mark complete"}
                    data-testid={`my-tasks-complete-${t.id}`}
                    onClick={(e) => { e.stopPropagation(); handleToggleComplete(t); }}
                    disabled={toggling === t.id}
                    className={`w-4 h-4 rounded-sm border flex items-center justify-center shrink-0 transition-colors ${
                      isDone
                        ? "bg-emerald-500/20 border-emerald-500/60 text-emerald-300"
                        : "border-zinc-600 hover:border-amber-500 text-transparent hover:text-amber-400"
                    }`}
                  >
                    {toggling === t.id ? <Loader2 className="w-3 h-3 animate-spin" /> : isDone ? "✓" : "✓"}
                  </button>
                  <span className={`px-1.5 py-0.5 rounded-sm text-[10px] font-mono uppercase border shrink-0 ${sb.cls}`}>
                    {sb.label}
                  </span>
                  {t.is_milestone ? (
                    <span
                      data-testid={`my-tasks-milestone-badge-${t.id}`}
                      className="px-1.5 py-0.5 rounded-sm text-[10px] font-mono uppercase border shrink-0 bg-violet-500/20 text-violet-200 border-violet-500/50 inline-flex items-center gap-1"
                      title="Milestone"
                    >
                      <Flag className="w-2.5 h-2.5" />
                      Milestone
                    </span>
                  ) : (
                    <span className={`px-1.5 py-0.5 rounded-sm text-[10px] font-mono uppercase border shrink-0 ${phaseCls}`}>
                      {t.phase || "—"}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => navigate(`/projects/${t.project_id}/plan?task=${t.id}`)}
                    className={`flex-1 min-w-0 text-left text-sm truncate ${
                      isDone ? "text-zinc-500 line-through" : "text-zinc-200"
                    }`}
                  >
                    {t.name}
                  </button>
                  <span className="hidden md:inline text-[11px] font-mono text-zinc-500 truncate max-w-[220px]">
                    {t.project_name}
                  </span>
                  <span
                    className={`text-[10px] font-mono uppercase tracking-wide flex items-center gap-1 shrink-0 w-[110px] justify-end ${
                      t.status === "late" ? "text-rose-300" : t.status === "due_soon" ? "text-amber-300" : "text-zinc-500"
                    }`}
                  >
                    {t.status === "late" && <AlertTriangle className="w-3 h-3" />}
                    {t.status !== "late" && t.due_date && <Clock className="w-3 h-3" />}
                    {offsetLabel(t)}
                  </span>
                  <span className="text-[10px] font-mono text-zinc-600 hidden lg:inline w-24 text-right">
                    {t.due_date || "—"}
                  </span>
                  <ChevronRight className="w-3.5 h-3.5 text-zinc-700 group-hover:text-amber-400 transition-colors shrink-0" />
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, tone, testId }) {
  const tones = {
    rose: "border-rose-500/30 bg-rose-500/5 text-rose-300",
    amber: "border-amber-500/30 bg-amber-500/5 text-amber-300",
    sky: "border-sky-500/30 bg-sky-500/5 text-sky-300",
    zinc: "border-zinc-800 bg-zinc-950/50 text-zinc-400",
    emerald: "border-emerald-500/30 bg-emerald-500/5 text-emerald-300",
  };
  return (
    <div
      data-testid={testId}
      className={`rounded-sm border px-3 py-2 ${tones[tone] || tones.zinc}`}
    >
      <div className="text-[10px] uppercase tracking-wide font-mono opacity-70">{label}</div>
      <div className="text-2xl font-light leading-tight mt-0.5 font-mono">{value}</div>
    </div>
  );
}
